<template>
<section class="pt-8 pt-md-5 pb-8 pb-md-14">
    <div class="container">
        <div class="row">
            <div class="col-12">
                <h1 class="mb-0 fw-bold">Connected Apps</h1>
                <p class="mb-6 text-muted">
                    Apps and AI assistants that you have connected to your DataLikers account via OAuth.
                    Disconnecting an app revokes its access immediately.
                </p>

                <Spinner v-if="!loaded" />

                <div v-else-if="loadError" class="text-danger py-6">
                    Couldn't load your connected apps. Refresh the page or try again later.
                </div>

                <div v-else-if="rows.length === 0" class="text-muted py-6">
                    You haven't connected any apps yet. Connect Claude or another MCP client by
                    pointing it at <code>{{ mcpUrl }}</code>.
                </div>

                <div v-else class="table-responsive">
                    <vue-good-table
                        :columns="columns"
                        :rows="rows"
                        :pagination-options="paginationOptions"
                        @page-change="onPageChange"
                    >
                        <template #table-row="props">
                            <span v-if="props.column.field === 'app'">
                                <strong>{{ props.row.client_name }}</strong>
                                <span v-if="props.row.is_cimd" class="badge bg-secondary ms-2" title="Client ID Metadata Document">CIMD</span>
                                <br>
                                <small class="text-muted">
                                    <a v-if="safeClientUri(props.row.client_uri)" :href="safeClientUri(props.row.client_uri)" target="_blank" rel="noopener noreferrer">
                                        {{ props.row.client_uri }}
                                    </a>
                                    <span v-else-if="props.row.client_uri" class="font-monospace">{{ props.row.client_uri }}</span>
                                    <span v-else class="font-monospace">{{ props.row.client_id }}</span>
                                </small>
                            </span>
                            <span v-else-if="props.column.field === 'actions'">
                                <button
                                    class="btn btn-danger mb-1 btn-xs"
                                    :disabled="disabled[props.row.grant_id]"
                                    @click="disconnect(props.row)"
                                >
                                    Disconnect
                                </button>
                            </span>
                            <span v-else>
                                {{ props.formattedRow[props.column.field] }}
                            </span>
                        </template>
                    </vue-good-table>
                </div>
            </div>
        </div>
    </div>
</section>
</template>

<script>
import { toDateTime } from "utils/date";
import Spinner from "../Spinner.vue";
import tablePagination from "../../mixins/tablePagination";

export default {
    components: {
        Spinner,
    },
    mixins: [tablePagination],
    data() {
        return {
            loaded: false,
            loadError: false,
            disabled: {},
            columns: [
                { label: "App", field: "app", sortable: false },
                { label: "Scope", field: "scope", sortable: false },
                { label: "Last authorized", field: "last_authorized_at", formatFn: toDateTime, sortable: false },
                { label: "Expires", field: "expires_at", formatFn: toDateTime, sortable: false },
                { label: "Actions", field: "actions", sortable: false },
            ],
            rows: [],
        };
    },
    computed: {
        mcpUrl() {
            return `https://mcp.${this.$state.siteDomain || location.hostname}/oauth/`;
        },
    },
    methods: {
        safeClientUri(url) {
            // Mirror backend _validate_metadata_url: https:// only, or http:// for loopback.
            // Defense-in-depth against legacy DB rows with phishing schemes/hosts predating the
            // backend validator landing in routers/oauth.py:_validate_metadata_url.
            //
            // The leading prefix check is required because WHATWG `new URL()` normalizes
            // hostless shorthands like `https:evil.example` and `https:///evil.example`
            // to `https://evil.example/` with hostname populated, while Python's urlparse
            // (used by the backend) rejects them as missing netloc. Match the strict form.
            if (typeof url !== 'string' || !url) return '';
            if (!/^https?:\/\/[^/?#]/i.test(url)) return '';
            try {
                const parsed = new URL(url);
                if (parsed.protocol === 'https:') return url;
                if (parsed.protocol === 'http:') {
                    const host = parsed.hostname.toLowerCase();
                    if (host === 'localhost' || host === '127.0.0.1' || host === '[::1]' || host === '::1') {
                        return url;
                    }
                }
                return '';
            } catch (_) {
                return '';
            }
        },
        fetch() {
            this.loadError = false;
            this.$services.OAuthGrants().get().then(resp => {
                this.rows = (resp.body && resp.body.grants) || [];
            }).catch(() => {
                this.rows = [];
                this.loadError = true;
            }).finally(() => {
                this.loaded = true;
            });
        },
        disconnect(row) {
            if (!confirm(`Disconnect "${row.client_name}"? Existing sessions will lose access.`)) return;
            this.disabled = { ...this.disabled, [row.grant_id]: true };
            this.$services.OAuthGrant().delete({ grant_id: row.grant_id }).then(() => {
                this.$notify.success({ title: 'Disconnected', message: `${row.client_name} can no longer access your account` });
                this.fetch();
            }).catch(err => {
                this.$notify.error({
                    title: 'Could not disconnect',
                    message: err.status === 404 ? 'This app is no longer connected.' : 'Try again in a moment.',
                });
                if (err.status === 404) this.fetch();
            }).finally(() => {
                const next = { ...this.disabled };
                delete next[row.grant_id];
                this.disabled = next;
            });
        },
        onPageChange() {
            this.fetch();
        },
    },
    created() {
        this.fetch();
    },
};
</script>
